import React, { useContext, useEffect, useRef, useState } from 'react';

import Slider from '@mui/material/Slider';
import { makeStyles } from '@mui/styles';
import { Form, Formik, FormikValues } from 'formik';
import DOMPurify from 'isomorphic-dompurify';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Checkbox from '@components/common/Formik/FormikCheckBox';
import Input from '@components/common/Formik/FormikInputField';
import Switch from '@components/common/Formik/FormikSwitch';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import Text from '@components/common/Text/Text';
import { S_DEVICE } from '@components/lpc/Constants';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import { tipChoices } from '@models/PotModel/tipChoices';
import { participationDataType, poolContributionType, potType } from '@propTypes/potTypes';
import potService from '@services/domain/Lpc/PotService';
import { getRoute, ROUTE } from '@services/http/Route';
import { scrollToRef } from '@services/utils/DomService';
import { computeFees } from '@services/utils/NumberService';
import ObjectService from '@services/utils/ObjectService';
import { clearItemFromStorage, getItemFromStorage, setItemToStorage } from '@services/utils/StorageService';
import { formatCurrency } from '@services/utils/StringService';
import { freeParticipationSchema, participationSchema } from '@validations/participationSchema';

import css from './ParticipateForm.module.scss';

type ParticipateFormProps = {
    onHide: () => void;
    potRef: string;
    pot: potType;
    user: { firstName: string; lastName: string; email: string };
    poolContribution: poolContributionType;
    commissionType: string;
    participationRetry: participationDataType;
};

const ParticipateForm = ({
    onHide,
    potRef,
    pot,
    user,
    poolContribution,
    commissionType,
    participationRetry,
}: ParticipateFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'lpc-authentication', 'common']);
    const { contributionType, contributionAmount } = poolContribution;
    const router = useRouter();
    const { addToast } = useToasts();
    const { width } = useWindowSize();
    const potContext = useContext(PotContext);
    const { updatedPot } = potContext;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isAnimating, setIsAnimating] = useState<boolean>(true);
    const [tipsPercentage, setTipsPercentage] = useState<number>(17);
    const [tipsAmount, setTipsAmount] = useState<number>(
        contributionType === 'FIXED' ? Number(((contributionAmount * tipsPercentage) / 100).toFixed(2)) : 0,
    );
    const [tipChoice, setTipChoice] = useState<string>(tipChoices.NO_TIP);
    const [coffeeAmount, setCoffeeAmount] = useState<number>(0);
    const [contribution, setContribution] = useState<number>(0);
    const topPageRef = useRef(null);
    const containerTipsRef = useRef(null);
    const formRef = useRef(null);
    const [showTipsInput, setShowTipsInput] = useState<boolean>(false);
    const [showTipsCoffee, setShowTipsCoffee] = useState<boolean>(true);

    const handleTextClick = () => {
        setShowTipsInput(true);
    };

    useEffect(() => {
        if (commissionType === 'TIPS_MODEL' && participationRetry) {
            setContribution(participationRetry.totalAndCom);
            setTipsAmount(participationRetry.tipsAmount);
            setTipChoice(participationRetry.tipChoice);
        }
        computeTipsAmount(contribution, tipsPercentage);
    }, [participationRetry]);

    useEffect(() => {
        if (
            getItemFromStorage('local', 'participationInfo') &&
            getItemFromStorage('local', 'participationInfo').uniq === potRef
        ) {
            setContribution(getItemFromStorage('local', 'participationInfo').totalAndCom);
            if (getItemFromStorage('local', 'participationInfo').tipChoice !== tipChoices.COFFEE) {
                setTipsAmount(getItemFromStorage('local', 'participationInfo').tipsAmount);
                setTipsPercentage(
                    calculatePercentageFromTips(
                        Number(getItemFromStorage('local', 'participationInfo').tipsAmount),
                        Number(getItemFromStorage('local', 'participationInfo').totalAndCom),
                    ),
                );
            } else {
                setCoffeeAmount(getItemFromStorage('local', 'participationInfo').tipsAmount);
            }
        }
    }, []);

    useEffect(() => {
        computeTipsAmount(contribution, tipsPercentage);
    }, [contribution]);

    useEffect(() => {
        if (contribution > 0 || tipsAmount > 0) {
            setTimeout(() => {
                containerTipsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 0);
        }
    }, [contribution, tipsAmount]);

    useEffect(() => {
        if (tipsPercentage === 0) {
            setCoffeeAmount(0);
            setTipChoice(tipChoices.NO_TIP);
        }
    }, [tipsPercentage]);

    const sliderValueLabel = () => {
        return `${tipsAmount.toFixed(2)} €`;
    };

    const calculatePercentageFromTips = (tipsAmountFromStorage: number, contributionFromStorage: number): number => {
        return Number(((tipsAmountFromStorage * 100) / contributionFromStorage).toFixed(2));
    };

    const computeTipsAmount = (fieldContribution, tipsPercentage) => {
        if (commissionType === 'TIPS_MODEL') {
            const roundedNumber = (
                ((fieldContribution !== '' ? fieldContribution : contribution) * tipsPercentage) /
                100
            ).toFixed(2);
            fieldContribution !== 0 && setTipsAmount(Number(roundedNumber));
        }
    };

    const getFinalTipsAmount = () => {
        if (commissionType === 'TIPS_MODEL') {
            return (Number(tipsAmount) + Number(coffeeAmount)).toFixed(2);
        }
        return 0;
    };

    const getFinalParticipationAmount = (contributionAmount) => {
        return (
            Number(contributionAmount !== '' ? contributionAmount : contribution) + Number(getFinalTipsAmount())
        ).toFixed(2);
    };

    const scrollTopWhenIsInvalid = () => {
        const rect = formRef.current.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const offset = rect.top + scrollTop - 100;
        window.scrollTo({
            top: offset,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        if (topPageRef.current) {
            scrollToRef(topPageRef, 56);
        }
    }, []);

    const useStyles = makeStyles({
        slider: {
            color: 'gray',
            height: 8,
            transition: 'all 0.5s ease-in-out',
            '& .MuiSlider-thumb': {
                height: 24,
                width: 24,
                backgroundColor: '#E22B76',
                border: 'unset',
            },
            '& .MuiSlider-track': {
                background: 'linear-gradient(to right,  #E22B76, #FAC700) !important',
                border: 'unset',
            },
            '& .MuiSlider-valueLabel::before': {
                width: 0,
            },
            '& .MuiSlider-valueLabel': {
                background: '#E22B76',
                borderRadius: 8,
            },
            '& .MuiSlider-mark ': {
                width: 0,
                height: 4,
                backgroundColor: '#111E3D',
            },
        },
    });
    const classes = useStyles();

    const getValidationSchema = () => {
        return !ObjectService.isEmpty(user) ? participationSchema : freeParticipationSchema;
    };

    const getInitialValues = () => {
        if (participationRetry) {
            return {
                firstName: participationRetry.firstName || '',
                lastName: participationRetry.lastName || '',
                email: participationRetry.email || '',
                address: participationRetry.address || '',
                contributionAmount:
                    contributionType === 'FIXED' ? contributionAmount : participationRetry.totalAndCom || '',
                hiddenContributionAmount:
                    contributionType === 'FIXED' ? contributionAmount : participationRetry.totalAndCom || '',
                tipsAmount: '',
                optInRequired: false,
                confirmNewsletter: false,
                hideName: participationRetry.hideName || false,
                hideContribution: participationRetry.hideContribution || false,
                askParticipantsForAddress: updatedPot.privacy?.askParticipantsForAddress,
            };
        } else {
            return {
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                email: user?.email || '',
                address: '',
                contributionAmount:
                    contributionType === 'FIXED'
                        ? contributionAmount
                        : contribution > 0 &&
                            commissionType === 'TIPS_MODEL' &&
                            ![20, 50, 150, 300].includes(contribution)
                          ? contribution
                          : contribution > 0 && commissionType !== 'TIPS_MODEL'
                            ? contribution
                            : '',
                tipsAmount: '',
                optInRequired: false,
                confirmNewsletter: false,
                askParticipantsForAddress: updatedPot.privacy?.askParticipantsForAddress,
            };
        }
    };

    const onSuccess = (values, res) => {
        const participation = {
            user: {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                confirmNewsletter: values.confirmNewsletter,
            },
            callback: res,
        };
        setItemToStorage('session', 'participate', participation);
        router.push(getRoute(ROUTE.LPC.POT.POT, potRef, ROUTE.LPC.POT.PAYMENT, res.orderId)).then();
    };

    const onSubmit = (values: {
        firstName: string;
        lastName: string;
        email: string;
        address: string;
        contributionAmount: number | string;
        tipsAmount: number | string;
        tipChoice?: string;
        optInRequired: boolean;
        confirmNewsletter: boolean;
    }) => {
        setIsLoading(true);
        values.tipsAmount = getFinalTipsAmount();
        values.tipChoice = tipChoice;
        values.contributionAmount = values.contributionAmount !== '' ? values.contributionAmount : contribution;
        potService
            .participate(potRef, values)
            .then((res) => onSuccess(values, res))
            .catch((err) => {
                addToast(t(`errors.${err.isDefault ? 'SMONEY_GENERIC_ERROR' : err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                });
                setIsLoading(false);
            });
    };

    const getTipsBlockVisibility = (values: FormikValues) => {
        return (values.contributionAmount !== '' || contribution > 0) && tipsAmount === 0 && showTipsCoffee;
    };
    const [sliderValue, setSliderValue] = useState<number>(0);
    const intervalRef = useRef(null);
    const [amountFromInput, setAmountFromInput] = useState<number>(0);
    const [hasAnimated, setHasAnimated] = useState<boolean>(false);

    useEffect(() => {
        if (!hasAnimated && isAnimating && (contribution > 0 || amountFromInput > 0)) {
            let currentValue = 0;
            intervalRef.current = setInterval(() => {
                currentValue += 1;
                if (currentValue <= tipsPercentage) {
                    setSliderValue(currentValue);
                } else {
                    clearInterval(intervalRef.current);
                    setIsAnimating(false);
                    setHasAnimated(true);
                }
            }, 100);

            return () => clearInterval(intervalRef.current);
        }
    }, [amountFromInput, contribution, isAnimating, tipsPercentage, hasAnimated]);

    const handleSliderChange = (event: any, newValue: any) => {
        if (isAnimating) {
            clearInterval(intervalRef.current);
            setIsAnimating(false);
        }
        setHasAnimated(true);
        setSliderValue(newValue);
    };

    const showTipsThanks = (values: FormikValues) => {
        return (values.contributionAmount !== '' || contribution > 0) && tipsAmount > 0 && !showTipsInput;
    };

    return (
        <div className={css.page_container} ref={topPageRef}>
            <div className={css.header} ref={formRef}>
                <h5 color={'txt-primary'}>{pot.title}</h5>
                <a
                    className={css.header__close}
                    onClick={() => {
                        onHide();
                        clearItemFromStorage('local', 'participationInfo');
                    }}
                >
                    {t('participate.pot-return-cta')}
                </a>
            </div>
            <div className={css.page_container__content}>
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={getValidationSchema()}
                    onSubmit={(values) => onSubmit(values)}
                    validateOnChange
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, isValid }) => (
                        <Form className={css.form}>
                            <>
                                <div className={css.form__content}>
                                    {/* {ObjectService.isEmpty(user) && ( */}
                                    {/*    <> */}
                                    {/*        <Link href={getRoute(`${ROUTE.LPC.AUTH.SIGN_IN_PARTICIPATE}${potRef}`)}> */}
                                    {/*            <Button */}
                                    {/*                variant={'secondary_muted'} */}
                                    {/*                customClass={css.form__submit__cta} */}
                                    {/*                width={'50%'} */}
                                    {/*            > */}
                                    {/*                {t('participate.cta.sign-in')} */}
                                    {/*            </Button> */}
                                    {/*        </Link> */}
                                    {/*        <Text customClass={css.divider} variant={'caption_01'} color={'txt-secondary'}> */}
                                    {/*            <span>{t('login.or')}</span> */}
                                    {/*        </Text> */}
                                    {/*    </> */}
                                    {/* )} */}
                                    {/* <div className={css.header__infos}> */}
                                    {/*    {imageService.getPotImage(pot) && ( */}
                                    {/*        <img src={imageService.getPotImage(pot)} alt="Image de la cagnotte" /> */}
                                    {/*    )} */}
                                    {/*
                                    {/* </div> */}

                                    <div className={css.form__container_input}>
                                        <Text variant={'body_03'} color={'txt-primary'}>
                                            {t('participate.identity-label')}
                                        </Text>
                                        <div className={css.form__container_input__input_Identity}>
                                            <div
                                                className={`${css.form__input} ${css.form__container_input__input_Identity__size}`}
                                            >
                                                <Input
                                                    name="firstName"
                                                    type="text"
                                                    label={t('participate.firstName-label')}
                                                    isFloatingLabel
                                                />
                                            </div>
                                            <div
                                                className={`${css.form__input} ${css.form__container_input__input_Identity__size}`}
                                            >
                                                <Input
                                                    name="lastName"
                                                    type="text"
                                                    label={t('participate.lastName-label')}
                                                    isFloatingLabel
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={css.form__input}>
                                        <Input
                                            name="email"
                                            type="email"
                                            label={t('participate.email-label')}
                                            isFloatingLabel
                                        />
                                    </div>
                                    {(values.email || values.firstName || values.lastName) && (
                                        <div className={css.form__switch}>
                                            <Switch
                                                defaultOn={values.hideName}
                                                name={'hideName'}
                                                customClass={css.form__switch__switcher}
                                            >
                                                {t('participate.name-label')}
                                            </Switch>
                                        </div>
                                    )}
                                    {updatedPot.privacy?.askParticipantsForAddress && (
                                        <div className={css.form__input}>
                                            <Input
                                                name="address"
                                                type="text"
                                                label={t('participate.address-label')}
                                                placeholder={t('participate.address-placeholder')}
                                                isFloatingLabel
                                            />
                                        </div>
                                    )}
                                    <div className={css.header__divider} />
                                    <>
                                        {contributionType !== 'FIXED' ? (
                                            <div className={css.form__buttons_container}>
                                                <Text variant={'body_03'} color={'txt-primary'}>
                                                    {t('participate.amount-label')}
                                                </Text>
                                                <div className={css.form__buttons}>
                                                    {commissionType === 'TIPS_MODEL' && (
                                                        <>
                                                            <Button
                                                                type={'button'}
                                                                customClass={`${css.form__buttons__one} ${contribution === 20 && css.form__buttons__active}`}
                                                                onClick={() => {
                                                                    setContribution(20);
                                                                    setIsAnimating(true);
                                                                    setAmountFromInput(0);
                                                                    setFieldValue('contributionAmount', '');
                                                                    setFieldValue('contributionAmountHidden', 20);
                                                                    setTipChoice(tipChoices.TIP);
                                                                }}
                                                            >
                                                                20 €
                                                            </Button>

                                                            <Button
                                                                type={'button'}
                                                                customClass={`${css.form__buttons__one} ${contribution === 50 && css.form__buttons__active}`}
                                                                onClick={() => {
                                                                    setContribution(50);
                                                                    setIsAnimating(true);
                                                                    setAmountFromInput(0);
                                                                    setFieldValue('contributionAmount', '');
                                                                    setFieldValue('contributionAmountHidden', 50);
                                                                }}
                                                            >
                                                                50 €
                                                            </Button>

                                                            <Button
                                                                type={'button'}
                                                                customClass={`${css.form__buttons__one} ${contribution === 150 && css.form__buttons__active}`}
                                                                onClick={() => {
                                                                    setContribution(150);
                                                                    setIsAnimating(true);
                                                                    setAmountFromInput(0);
                                                                    setFieldValue('contributionAmount', '');
                                                                    setFieldValue('contributionAmountHidden', 150);
                                                                }}
                                                            >
                                                                150 €
                                                            </Button>
                                                            <Button
                                                                type={'button'}
                                                                customClass={`${css.form__buttons__one} ${contribution === 300 && css.form__buttons__active}`}
                                                                onClick={() => {
                                                                    setContribution(300);
                                                                    setIsAnimating(true);
                                                                    setAmountFromInput(0);
                                                                    setFieldValue('contributionAmount', '');
                                                                    setFieldValue('contributionAmountHidden', 300);
                                                                }}
                                                            >
                                                                300 €
                                                            </Button>
                                                        </>
                                                    )}
                                                    <div
                                                        className={
                                                            commissionType === 'TIPS_MODEL'
                                                                ? css.form__custom_input_solidarity
                                                                : css.form__custom_input
                                                        }
                                                    >
                                                        <Input
                                                            customStyle={'ui_primary_border'}
                                                            name="contributionAmount"
                                                            type="number"
                                                            isAmount
                                                            placeholder={
                                                                contributionType === 'SUGGESTED'
                                                                    ? t('participate.amount-suggested-placeholder', {
                                                                          suggestedAmount: contributionAmount,
                                                                      })
                                                                    : commissionType === 'TIPS_MODEL'
                                                                      ? t('participate.amount-placeholder-solidarity')
                                                                      : t('participate.amount-placeholder')
                                                            }
                                                            onHandleChange={(e) => {
                                                                computeTipsAmount(e.target.value, tipsPercentage);
                                                                setTipChoice(tipChoices.TIP);
                                                                setAmountFromInput(e.target.value);
                                                                setFieldValue(
                                                                    'contributionAmountHidden',
                                                                    e.target.value,
                                                                );
                                                                setContribution(0);
                                                                setIsAnimating(true);
                                                            }}
                                                        />
                                                    </div>
                                                    <Input
                                                        customStyle={'ui_primary_border'}
                                                        name="contributionAmountHidden"
                                                        type="hidden"
                                                        isAmount
                                                    />
                                                </div>
                                                {(contribution || values.contributionAmount) && (
                                                    <div className={css.form__switch}>
                                                        <Switch
                                                            name={'hideContribution'}
                                                            defaultOn={values.hideContribution}
                                                        >
                                                            {t('participate.contribution-label')}
                                                        </Switch>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className={css.form__custom_input_disabled}>
                                                <Input
                                                    customStyle={'ui_primary_border'}
                                                    name="contributionAmount"
                                                    type="number"
                                                    label={t('participate.amount-placeholder-fixed')}
                                                    isFloatingLabel
                                                    isAmount
                                                    disabled
                                                />
                                            </div>
                                        )}
                                        {commissionType === 'TIPS_MODEL' &&
                                            (contribution || values.contributionAmount) && (
                                                <>
                                                    <div className={css.form__slider_container}>
                                                        <Image
                                                            src="/front-static/icons/pot/participate-heart.svg"
                                                            alt="participate-heart"
                                                            height={50}
                                                            width={50}
                                                            style={{ margin: '-38px 0 0 -38px' }}
                                                        />
                                                        <Text variant={'label'} color={'txt-primary'}>
                                                            {t('participate.tips.title')}
                                                        </Text>
                                                        <Text
                                                            variant={'footer'}
                                                            color={'txt-primary'}
                                                            htmlText={t('participate.tips.description')}
                                                            html
                                                        />
                                                        <div className={css.form__slider_container__slider}>
                                                            <Slider
                                                                ref={containerTipsRef}
                                                                aria-label="Percentage"
                                                                defaultValue={0}
                                                                value={sliderValue}
                                                                valueLabelFormat={() => sliderValueLabel()}
                                                                valueLabelDisplay="on"
                                                                step={1}
                                                                classes={{
                                                                    root: classes.slider,
                                                                }}
                                                                marks
                                                                min={0}
                                                                max={30}
                                                                disabled={
                                                                    values.contributionAmount === '' &&
                                                                    contribution === 0
                                                                }
                                                                onChange={(event, value: number) => {
                                                                    setShowTipsCoffee(true);
                                                                    handleSliderChange(event, value);
                                                                    computeTipsAmount(values.contributionAmount, value);
                                                                    setTipChoice(tipChoices.TIP);
                                                                    setTipsPercentage(value);
                                                                    setCoffeeAmount(0);
                                                                }}
                                                            />
                                                        </div>
                                                        <Text
                                                            variant={'caption_03'}
                                                            color={'txt-primary'}
                                                            customClass={`${css.form__slider_container__slider__thanks} ${
                                                                showTipsThanks(values)
                                                                    ? css.form__slider_container__slider__thanks__open
                                                                    : css.form__slider_container__slider__thanks__close
                                                            }`}
                                                        >
                                                            {t('participate.tips.thanks')}
                                                        </Text>
                                                        {!showTipsInput ? (
                                                            <div
                                                                onClick={handleTextClick}
                                                                className={
                                                                    css.form__slider_container__slider__custom_tips_input
                                                                }
                                                            >
                                                                <Text
                                                                    variant={'caption_03'}
                                                                    color={'txt-primary'}
                                                                    customClass={`${css.form__slider_container__slider__thanks} ${
                                                                        showTipsThanks(values)
                                                                            ? css.form__slider_container__slider__thanks__open
                                                                            : css.form__slider_container__slider__thanks__close
                                                                    }`}
                                                                >
                                                                    {t('participate.tips.custom-input-tips')}
                                                                </Text>
                                                            </div>
                                                        ) : (
                                                            tipsAmount !== 0 && (
                                                                <Input
                                                                    customStyle={'ui_primary_border'}
                                                                    name="tipsAmount"
                                                                    type="number"
                                                                    customCssStyle={{
                                                                        height: '38px',
                                                                        width: '65%',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                    isAmount
                                                                    placeholder={t(
                                                                        'participate.tips.custom-input-tips-placeholder',
                                                                    )}
                                                                    onHandleBlur={(e) => {
                                                                        if (e.target.value === '') {
                                                                            setShowTipsInput(false);
                                                                            setTipsPercentage(0);
                                                                        }
                                                                    }}
                                                                    onHandleChange={(e) => {
                                                                        setTipsAmount(Number(e.target.value));
                                                                        setFieldValue('tipsAmount', e.target.value);
                                                                        setSliderValue(
                                                                            calculatePercentageFromTips(
                                                                                Number(e.target.value),
                                                                                contribution > 0
                                                                                    ? Number(contribution)
                                                                                    : Number(values.contributionAmount),
                                                                            ),
                                                                        );
                                                                    }}
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                    <div
                                                        className={`${css.form__coffee_container} ${getTipsBlockVisibility(values) ? css.form__coffee_container__open : css.form__coffee_container__close}`}
                                                    >
                                                        {getTipsBlockVisibility(values) && (
                                                            <>
                                                                <Text variant={'label'} color={'txt-primary'}>
                                                                    {t('participate.tips.coffee-title')}
                                                                </Text>
                                                                <Text variant={'body_02'} color={'txt-primary'}>
                                                                    {t('participate.tips.coffee-sub-title')}
                                                                </Text>
                                                                <div className={css.form__coffee_container__buttons}>
                                                                    <Button
                                                                        type={'button'}
                                                                        customClass={`${css.form__coffee_container__buttons__one} ${coffeeAmount === 1.2 && css.form__coffee_container__buttons__active}`}
                                                                        onClick={() => {
                                                                            setCoffeeAmount(
                                                                                coffeeAmount === 1.2 ? 0 : 1.2,
                                                                            );
                                                                            setTipChoice(
                                                                                coffeeAmount === 0
                                                                                    ? tipChoices.COFFEE
                                                                                    : tipChoices.NO_TIP,
                                                                            );
                                                                            setFieldValue('tipsAmount', '');
                                                                        }}
                                                                    >
                                                                        {t('participate.tips.coffee-1')}
                                                                    </Button>
                                                                    <div
                                                                        className={
                                                                            css.form__coffee_container__input_coffee
                                                                        }
                                                                    >
                                                                        <Input
                                                                            customStyle={'ui_primary_border'}
                                                                            name="tipsAmount"
                                                                            type="number"
                                                                            customCssStyle={{ height: '38px' }}
                                                                            isAmount
                                                                            placeholder={t(
                                                                                'participate.amount-placeholder-coffee',
                                                                            )}
                                                                            onHandleChange={(e) => {
                                                                                setCoffeeAmount(Number(e.target.value));
                                                                                setTipChoice(
                                                                                    e.target.value
                                                                                        ? tipChoices.COFFEE
                                                                                        : tipChoices.NO_TIP,
                                                                                );
                                                                                setFieldValue(
                                                                                    'tipsAmount',
                                                                                    e.target.value,
                                                                                );
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {getTipsBlockVisibility(values) && (
                                                                    <div
                                                                        onClick={() => {
                                                                            setShowTipsCoffee(false);
                                                                            setTipChoice(tipChoices.REMIND_ME_LATER);
                                                                        }}
                                                                        className={
                                                                            css.form__slider_container__slider__maybe_later
                                                                        }
                                                                    >
                                                                        <Text
                                                                            variant={'caption_03'}
                                                                            color={'txt-primary'}
                                                                            customClass={`${
                                                                                getTipsBlockVisibility(values)
                                                                                    ? css.form__slider_container__maybe_later__open
                                                                                    : css.form__slider_container__maybe_later__close
                                                                            }`}
                                                                        >
                                                                            {t('participate.tips.maybe-later')}
                                                                        </Text>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                    </>

                                    {!updatedPot.commissionIncluded && values.contributionAmount && false && (
                                        <>
                                            <div className={css.form__fees_container}>
                                                <Text variant={'caption_00'} color={'txt-primary'}>
                                                    {t('participate.fees')}
                                                </Text>
                                                <Text variant={'caption_00'} color={'txt-primary'}>
                                                    {formatCurrency(computeFees(values.contributionAmount, updatedPot))}
                                                </Text>
                                            </div>
                                            <div className={css.form__fees_container}>
                                                <Text variant={'caption_00'} color={'txt-primary'}>
                                                    {t('participate.total')}
                                                </Text>
                                                <Text variant={'caption_00'} color={'txt-primary'}>
                                                    {formatCurrency(
                                                        computeFees(values.contributionAmount, updatedPot) +
                                                            Number(values.contributionAmount),
                                                    )}
                                                </Text>
                                            </div>
                                        </>
                                    )}

                                    <div className={css.form__checkbox}>
                                        {/* <div className={css.form__input}> */}
                                        {/*    <Checkbox name="hideContribution"> */}
                                        {/*        {t('participate.contribution-label')} */}
                                        {/*    </Checkbox> */}
                                        {/* </div> */}
                                        {/* <div className={css.form__input}> */}
                                        {/*    <Checkbox name="hideName">{t('participate.name-label')}</Checkbox> */}
                                        {/* </div> */}
                                        {ObjectService.isEmpty(user) && (
                                            <>
                                                <div className={css.form__input}>
                                                    <Checkbox name="confirmNewsletter">
                                                        {t('participate.newsletter-label')}
                                                    </Checkbox>
                                                </div>
                                                <div className={css.form__input}>
                                                    <Checkbox name="optInRequired">
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    t('participate.cgu-label', {
                                                                        termsLpc: ROUTE.LPC.CGU,
                                                                        termsMangopay: ROUTE.LPC.MANGO_PAY_CGU,
                                                                        privacyPolicyLPC: ROUTE.LPC.PRIVACY_POLITY,
                                                                        privacyPolicyMangopay:
                                                                            'https://mangopay.com/privacy-statement',
                                                                    }),
                                                                    { ADD_ATTR: ['target'] },
                                                                ),
                                                            }}
                                                        />
                                                    </Checkbox>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className={css.form__submit}>
                                        <Button
                                            onClick={() => {
                                                setFieldValue(
                                                    'contributionAmountHidden',
                                                    contribution || values.contributionAmount,
                                                );
                                                if (!isValid) {
                                                    scrollTopWhenIsInvalid();
                                                }
                                            }}
                                            type={'submit'}
                                            variant={'primary'}
                                            customClass={css.form__submit__cta}
                                            isLoading={isLoading}
                                            isDisabled={isLoading}
                                        >
                                            <div className={css.form__submit__first_line}>
                                                <img src="/front-static/icons/display/secure.png" alt="secure" />
                                                <Text variant={'label'} color={'white'}>
                                                    {width > S_DEVICE
                                                        ? t('participate.cta.participate')
                                                        : t('participate.cta.mobile-participate')}{' '}
                                                    {getFinalParticipationAmount(values.contributionAmount)} €
                                                </Text>
                                            </div>
                                            {commissionType === 'TIPS_MODEL' && (
                                                <Text variant={'footer'} color={'white'}>
                                                    {t('participate.cta.participate-tips', {
                                                        tipsAmount: getFinalTipsAmount(),
                                                    })}
                                                </Text>
                                            )}
                                        </Button>
                                        <Text
                                            customClass={css.form__terms}
                                            variant={'caption_02'}
                                            color={'ui-secondary'}
                                        >
                                            <img src="/front-static/icons/display/secure.svg" alt="secure" />
                                            {t('participate.terms')}
                                        </Text>
                                    </div>
                                </div>
                            </>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ParticipateForm;
